import { BankDetailsFormFields } from '@pages/CompanyProfile/components/CompanyProfileModules/BankDetailsModule/types';
import { PasswordFormFields } from '@pages/CompanyProfile/components/CompanyProfileModules/PasswordModule/types';
import { RepresentativesFormFields } from '@pages/CompanyProfile/components/CompanyProfileModules/RepresentativesListModule/types';
import {
  CompanyProfileExtendedFormFields,
  CompanyProfileFormFields,
} from '@pages/CompanyProfile/components/CompanyProfileModulesWrapper/types';
import { $error } from '@settings/errorContext';
import { Logger, LoggerFactory } from '@settings/services/logger/Logger';
import axios from 'axios';

import {
  CompanyProfileServiceInterface,
  DeleteBankDetailsResponse,
  DeleteRepresentativeResponse,
  LoadDataError,
  SaveAccessResponse,
  SaveBankDetailsResponse,
  SaveCompanyResponse,
  SaveRepresentativeResponse,
  UploadDocumentParams,
  UploadDocumentResponse,
  UploadRequiredDocumentParams,
  UploadRequiredDocumentResponse,
} from './interface';
import { AccessData } from './types/accessData';
import { DocumentsData } from './types/documentsData';
import { MainData } from './types/mainData';
import { RepresentativeData } from './types/representativesData';

/**
 * Сервис для работы с профилем компании bitrix
 */
export class CompanyProfileService implements CompanyProfileServiceInterface {
  private readonly logger: Logger;

  private readonly bitrixEndpoint: string;

  private readonly token: string;

  /**
   * Конструктор сервиса
   *
   * @param logger
   * @param bitrixEndpoint
   * @param token
   */
  constructor(logger: LoggerFactory, bitrixEndpoint: string, token?: string) {
    this.logger = logger.make(`CompanyProfileService`);
    this.bitrixEndpoint = bitrixEndpoint;
    this.token = `${token}`;
  }

  /**
   * Загрузка данных главной табы
   * @param userId
   */
  async LoadMainData(userId: string): Promise<MainData | LoadDataError> {
    try {
      const result = await axios.post<MainData | LoadDataError>(
        this.bitrixEndpoint + '/company.settings/ajax.php',
        { user_id: userId, action: 'init' },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Load main data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Загрузка данных главной табы
   * @param userId
   */
  async LoadDocumentsData(userId: string): Promise<DocumentsData | LoadDataError> {
    try {
      const result = await axios.post<DocumentsData | LoadDataError>(
        this.bitrixEndpoint + '/company.settings.documents/ajax.php',
        { user_id: userId, action: 'init' },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Load documents data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Загрузка данных табы предствителей
   * @param userId
   */
  async LoadRepresentativesData(userId: string): Promise<RepresentativeData | LoadDataError> {
    try {
      const result = await axios.post<RepresentativeData | LoadDataError>(
        this.bitrixEndpoint + '/company.settings.representatives/ajax.php',
        { user_id: userId, action: 'init' },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Load representatives data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Загрузка данных табы предствителей
   * @param userId
   * @param companyType
   * @param payload
   */
  async SaveCompany(
    userId: string,
    companyType: string,
    payload: CompanyProfileFormFields & CompanyProfileExtendedFormFields
  ): Promise<SaveCompanyResponse> {
    const formattedPayload: Record<string, unknown> = {
      user_id: userId,
      action: 'update',
      short_name: payload.shortName,
      full_name: payload.fullName,
      //   inn: payload.
      kpp: payload.kpp,
      // orgn: payload
      legal_address: payload.legalAddress,
      postal_address: payload.postalAddress,
      //   country: payload.
      currency: payload.settlementsCurrency,

      director: payload.representativeFullName,
      job_position: payload.jobTitle,
      basis: payload.actionReason,

      edo: payload.availabilityEdo,
      edo_operator: payload.operatorEdo,
    };

    if (companyType === '2') {
      formattedPayload.first_name = payload.firstName;
      formattedPayload.last_name = payload.lastName;
      formattedPayload.second_name = payload.secondName;
    }

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && typeof value === 'object') {
        formattedPayload[key] = value.id;
      }
    });

    try {
      const result = await axios.post<SaveCompanyResponse>(
        this.bitrixEndpoint + '/company.settings.main/ajax.php',
        { ...formattedPayload },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Save company data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Сохранение данных представителя
   * @param userId
   * @param payload
   */
  async SaveRepresentative(
    userId: string,
    payload: RepresentativesFormFields
  ): Promise<SaveRepresentativeResponse> {
    const formattedPayload: Record<string, unknown> = {
      user_id: userId,
      action: 'update',
      id: payload.id,
      full_name: payload.fullName,
      email: payload.email,
      role: payload.accessRight,
      work_type: payload.workingWithApplications,
    };

    if (payload.id.includes('new-representative')) {
      formattedPayload.action = 'add';
      delete formattedPayload.id;
    }

    try {
      const result = await axios.post<SaveRepresentativeResponse>(
        this.bitrixEndpoint + '/company.settings.representatives/ajax.php',
        { ...formattedPayload },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Save representative data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Удаление представителя
   * @param userId
   * @param id
   */
  async DeleteRepresentative(userId: string, id: string): Promise<DeleteRepresentativeResponse> {
    try {
      const result = await axios.post<DeleteRepresentativeResponse>(
        this.bitrixEndpoint + '/company.settings.representatives/ajax.php',
        { user_id: userId, action: 'delete', id },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Delete representative data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Загрузка данных табы доступа
   * @param userId
   * @param action
   */
  async LoadAccessData(userId: string): Promise<AccessData | LoadDataError> {
    try {
      const result = await axios.post<AccessData | LoadDataError>(
        this.bitrixEndpoint + '/company.settings.access/ajax.php',
        { user_id: userId, action: 'init' },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Load access data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Сохранение данных пароля
   * @param userId
   * @param payload
   */
  async SaveAccess(userId: string, payload: PasswordFormFields): Promise<SaveAccessResponse> {
    const formattedPayload: Record<string, unknown> = {
      user_id: userId,
      action: 'update',
      password: payload.currentPassword,
      new_password: payload.newPassword,
    };

    try {
      const result = await axios.post<SaveRepresentativeResponse>(
        this.bitrixEndpoint + '/company.settings.access/ajax.php',
        { ...formattedPayload },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Save access data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Загрузка документа
   * @param userId
   * @param id
   */
  async UploadDocument(
    userId: string,
    payload: UploadDocumentParams
  ): Promise<UploadDocumentResponse> {
    const data = new FormData();
    data.append(payload.fieldCode, payload.file);
    data.append('user_id', userId);
    data.append('action', 'save_file');

    try {
      const result = await axios.post<UploadDocumentResponse>(
        this.bitrixEndpoint + '/company.settings.documents/ajax.php',
        data,
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Uploaded document file`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Удаление представителя
   * @param userId
   * @param id
   */
  async DeleteBankDetails(userId: string, id: string): Promise<DeleteBankDetailsResponse> {
    try {
      const result = await axios.post<DeleteBankDetailsResponse>(
        this.bitrixEndpoint + '/company.settings.main/ajax.php',
        { user_id: userId, action: 'delete_bank_detail', id },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Delete bank details data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Сохранение данных банковского реквизита
   * @param userId
   * @param payload
   */
  async SaveBankDetails(
    userId: string,
    payload: BankDetailsFormFields
  ): Promise<SaveBankDetailsResponse> {
    const formattedPayload: Record<string, unknown> = {
      user_id: userId,
      action: 'update_bank_detail',
      id: payload.id,
      name: payload.bankName,
      address: payload.bankAddress,
      acc_num: payload.currentAccount,
      cor_acc_num: payload.correspondentAccount,
      acc_currency: payload.currency,
      bik: payload.bik,
      swift: payload.swift,
      is_default: payload.defaultDetails,
    };

    if (payload.id.includes('new-bank-details')) {
      formattedPayload.action = 'add_bank_detail';
      delete formattedPayload.id;
    }

    try {
      const result = await axios.post<SaveBankDetailsResponse>(
        this.bitrixEndpoint + '/company.settings.main/ajax.php',
        { ...formattedPayload },
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Save bank details data`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }

  /**
   * Загрузка обязательного документа
   * @param userId
   * @param id
   */
  async UploadRequiredDocument(
    userId: string,
    payload: UploadRequiredDocumentParams
  ): Promise<UploadRequiredDocumentResponse> {
    const data = new FormData();
    data.append('file', payload.file);
    data.append('user_id', userId);
    data.append('action', 'save_file');

    try {
      const result = await axios.post<UploadRequiredDocumentResponse>(
        this.bitrixEndpoint + '/company.settings.main/ajax.php',
        data,
        {
          headers: {
            // Authorization: this.token,
          },
        }
      );

      this.logger.Debug(`Uploaded required document file`, result.data);
      return result.data;
    } catch (e) {
      this.logger.Error(`Error:`, e);
      $error.next(e);
    }
  }
}
